import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Snowflake,
  Thermometer,
  Shield,
  Clock,
  Star,
  Phone,
  MessageCircle,
  FileCheck2
} from "lucide-react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Parallax } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/parallax';

const features = [
  {
    name: "Instalação Profissional",
    description: "Instalação especializada com equipe certificada, garantia estendida e suporte técnico completo para todos os tipos de ambiente.",
    icon: Thermometer,
    gradient: "from-blue-500 to-cyan-400"
  },
  {
    name: "Manutenção Preventiva",
    description: "Programa completo de manutenção preventiva para maximizar a eficiência energética e prolongar a vida útil dos equipamentos.",
    icon: Shield,
    gradient: "from-cyan-500 to-blue-400"
  },
  {
    name: "Suporte 24/7",
    description: "Atendimento de emergência disponível 24 horas por dia, 7 dias por semana, com técnicos especializados prontos para atender.",
    icon: Clock,
    gradient: "from-blue-600 to-indigo-500"
  },
  {
    name: "PMOC Certificado",
    description: "Plano de Manutenção, Operação e Controle totalmente em conformidade com as normas ANVISA e legislação vigente.",
    icon: CheckCircle2,
    gradient: "from-indigo-500 to-purple-500"
  },
];

const testimonials = [
  {
    name: "Maria Silva",
    company: "Residencial Premium",
    text: "Excelente atendimento e instalação impecável. O ar condicionado funciona perfeitamente há mais de 2 anos sem problemas.",
    rating: 5
  },
  {
    name: "João Santos",
    company: "Empresa Tech Solutions",
    text: "Profissionais altamente qualificados. Fizeram a manutenção preventiva de todo nosso escritório com eficiência total.",
    rating: 5
  },
  {
    name: "Ana Costa",
    company: "Clínica Médica",
    text: "O PMOC foi executado conforme todas as normas. Recomendo para qualquer estabelecimento que precisa de conformidade.",
    rating: 5
  }
];

const stats = [
  { number: "500+", label: "Projetos Concluídos", icon: CheckCircle2 },
  { number: "30+", label: "Anos de Experiência", icon: Star },
  { number: "24/7", label: "Suporte Técnico", icon: Clock },
  { number: "100%", label: "Clientes Satisfeitos", icon: Shield }
];

export function Home() {
  return (
    <React.Fragment>
      <Helmet>
        <title>Arcgel - Soluções em Climatização</title>
        <meta
          name="description"
          content="Arcgel - Soluções completas em climatização para sua casa ou empresa. Instalação, manutenção e venda de equipamentos de ar condicionado."
        />
      </Helmet>

      {/* Hero Section - Tom Base */}
      <section className="relative overflow-hidden min-h-screen flex items-center justify-center py-20">
        {/* Elementos gráficos sem overlay */}
        <div className="absolute inset-0 z-0">

          {/* Formas diagonais suaves - estilo aurora */}
          <div className="absolute inset-0">
            {/* Camada 1 - Diagonal principal do canto superior esquerdo */}
            <div 
              className="absolute inset-0 opacity-[0.07]"
              style={{
                background: 'linear-gradient(135deg, transparent 0%, transparent 30%, rgba(6, 182, 212, 0.3) 45%, rgba(59, 130, 246, 0.2) 55%, transparent 70%, transparent 100%)',
              }}
            />
            
            {/* Camada 2 - Diagonal secundária mais baixa */}
            <div 
              className="absolute inset-0 opacity-[0.05]"
              style={{
                background: 'linear-gradient(140deg, transparent 0%, transparent 50%, rgba(6, 182, 212, 0.4) 65%, rgba(14, 165, 233, 0.3) 75%, transparent 90%, transparent 100%)',
              }}
            />
            
            {/* Camada 3 - Diagonal sutil no topo */}
            <div 
              className="absolute inset-0 opacity-[0.04]"
              style={{
                background: 'linear-gradient(125deg, rgba(59, 130, 246, 0.2) 0%, transparent 25%, transparent 100%)',
              }}
            />
            
            {/* Camada 4 - Curva suave inferior */}
            <div 
              className="absolute inset-0 opacity-[0.06]"
              style={{
                background: 'linear-gradient(150deg, transparent 0%, transparent 60%, rgba(6, 182, 212, 0.25) 80%, rgba(30, 64, 175, 0.2) 100%)',
              }}
            />
            
            {/* Camada 5 - Brilho sutil central-direito */}
            <div 
              className="absolute inset-0 opacity-[0.08]"
              style={{
                background: 'radial-gradient(ellipse 80% 50% at 70% 40%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)',
              }}
            />
          </div>

          {/* Elementos gráficos animados */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Flocos de neve animados com fade out */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -100,
                  rotate: 0,
                  opacity: 0.3
                }}
                animate={{
                  y: window.innerHeight * 0.85, // Para antes de chegar na próxima seção
                  rotate: 360,
                  opacity: [0.3, 0.7, 0.5, 0.2, 0] // Fade out gradual
                }}
                transition={{
                  duration: 12 + Math.random() * 8, // Duração mais curta
                  repeat: Infinity,
                  delay: Math.random() * 5,
                  ease: "easeOut" // Easing mais suave
                }}
              >
                <Snowflake
                  className={`text-cyan-400/30 ${i % 2 === 0 ? 'w-8 h-8' : 'w-6 h-6'}`}
                />
              </motion.div>
            ))}

            {/* Círculos de luz */}
            <motion.div
              className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-3xl"
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.4, 0.7, 0.4]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            />
          </div>


        </div>

        {/* Conteúdo principal */}
        <div className="container relative z-10 text-center px-4 pt-20 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-5xl mx-auto"
          >
            {/* Badge superior */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 backdrop-blur-sm mb-8"
            >
              <Star className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-100">
                Mais de 30 anos de experiência
              </span>
            </motion.div>

            {/* Título principal */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="text-5xl md:text-7xl lg:text-8xl font-black leading-tight mb-6"
            >
              <span className="text-white drop-shadow-2xl">
                Climatização
              </span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Profissional
              </span>
            </motion.h1>

            {/* Subtítulo */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="text-xl md:text-2xl text-slate-200 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Soluções completas em ar condicionado e refrigeração para
              <span className="text-cyan-300 font-semibold"> residências</span> e
              <span className="text-cyan-300 font-semibold"> empresas</span>
            </motion.p>


            {/* Botões de ação */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link
                to="/contato"
                className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
              >
                <Phone className="w-5 h-5" />
                Solicitar Orçamento
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>

              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Indicador de scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-white/60 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Marcas Parceiras - Sem Background */}
      <section className="py-20 md:py-32 relative">

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Marcas Parceiras
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Trabalhamos exclusivamente com as marcas líderes do mercado mundial em climatização
            </p>
          </motion.div>

          <div className="relative max-w-6xl mx-auto">
            {/* Botões de navegação customizados */}
            <button className="swiper-button-prev-custom absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center group hover:scale-110" aria-label="Anterior">
              <ArrowRight className="w-5 h-5 text-white rotate-180 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <button className="swiper-button-next-custom absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center group hover:scale-110" aria-label="Próximo">
              <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-0.5 transition-transform" />
            </button>

            <Swiper
              modules={[Navigation, Autoplay, Parallax]}
              spaceBetween={24}
              slidesPerView={2}
              breakpoints={{
                640: { slidesPerView: 3 },
                768: { slidesPerView: 4 },
                1024: { slidesPerView: 5 },
                1280: { slidesPerView: 6 },
              }}
              navigation={{
                prevEl: '.swiper-button-prev-custom',
                nextEl: '.swiper-button-next-custom',
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }}
              loop
              parallax
              className="px-16"
            >
              {[
                'Logotipo-Carrier-Ar-Condicionado-arc-gel.png',
                'Logotipo-Consul-Ar-Condicionado-arc-gel.png',
                'Logotipo-Daikin-Ar-Condicionado-arc-gel.png',
                'Logotipo-Electrolux-Ar-Condicionado-arc-gel.png',
                'Logotipo-Elgin-Ar-Condicionado-arc-gel.png',
                'Logotipo-Gree-Ar-Condicionado-arc-gel.png',
              ].map((logo, index) => (
                <SwiperSlide key={logo}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group relative"
                  >
                    <div className="h-32 p-6 rounded-2xl border border-white/10 hover:border-cyan-400/50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-cyan-500/10 flex items-center justify-center group-hover:scale-105">
                      <img
                        src={`/assets/${logo}`}
                        alt={logo.replace('Logotipo-', '').replace('-Ar-Condicionado-arc-gel', '').replace('.png', '') + ' logo'}
                        className="max-h-16 w-auto object-contain filter brightness-90 group-hover:brightness-110 transition-all duration-300"
                        loading="lazy"
                      />

                      {/* Efeito de brilho no hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/10 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* Features Section - Nossos Serviços */}
      <section className="py-20 md:py-32 relative overflow-hidden">

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Nossos <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Serviços</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Soluções completas em climatização com tecnologia de ponta e atendimento personalizado
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={feature.name}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="group relative"
                >
                  {/* Card principal */}
                  <div className="relative h-full p-8 rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10">
                    {/* Ícone com gradiente */}
                    <div className="mb-6">
                      <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.gradient} shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Conteúdo */}
                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                      {feature.name}
                    </h3>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      {feature.description}
                    </p>

                    {/* Link */}
                    <Link
                      to="/servicos"
                      className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium transition-colors group-hover:gap-3"
                    >
                      Saiba mais
                      <ArrowRight className="w-4 h-4 transition-transform" />
                    </Link>

                    {/* Efeito de brilho no hover */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PMOC Section */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-500/10" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              PMOC <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Certificado</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Plano de Manutenção, Operação e Controle em total conformidade com as normas ANVISA e Ministério da Saúde
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Card 1 - Conformidade Legal */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group relative"
            >
              <div className="relative h-full p-8 rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 bg-slate-800/30 backdrop-blur-sm">
                <div className="mb-6">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                    <CheckCircle2 className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                  Conformidade Legal 100%
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Atendemos rigorosamente a Portaria MS nº 3.523/98, Resolução ANVISA RE nº 09/2003 e NBR 16401. 
                  Garantia total de conformidade legal.
                </p>
                
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/0 via-green-500/5 to-emerald-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>

            {/* Card 2 - Qualidade do Ar */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative"
            >
              <div className="relative h-full p-8 rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 bg-slate-800/30 backdrop-blur-sm">
                <div className="mb-6">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 shadow-lg">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                  Qualidade do Ar Garantida
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Monitoramento contínuo de CO₂, temperatura, umidade e análise microbiológica. 
                  Ambiente saudável e seguro para todos.
                </p>
                
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-cyan-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>

            {/* Card 3 - Documentação Completa */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group relative"
            >
              <div className="relative h-full p-8 rounded-2xl border border-slate-700/50 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 bg-slate-800/30 backdrop-blur-sm">
                <div className="mb-6">
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 shadow-lg">
                    <FileCheck2 className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors">
                  Documentação Completa
                </h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  Livro de registros, certificados, laudos técnicos e cronogramas. 
                  Toda documentação necessária para fiscalizações.
                </p>
                
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/0 via-purple-500/5 to-violet-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </motion.div>
          </div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/pmoc"
                className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center relative overflow-hidden"
              >
                <span className="relative z-10">Saiba Mais sobre PMOC</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform relative z-10" />
                
                {/* Efeito de brilho */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>

              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border border-white/30 hover:bg-green-600/20 hover:border-green-400/60 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Estatísticas Section - Tom Mais Claro */}
      <section className="py-20 md:py-32 relative">

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Números que <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Impressionam</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Nossa experiência e dedicação refletidas em resultados concretos
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="relative mb-4">
                    <div className="inline-flex p-4 rounded-2xl border border-cyan-400/30 group-hover:border-cyan-400/60 transition-all duration-300">
                      <IconComponent className="w-8 h-8 text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {stat.number}
                  </div>
                  <div className="text-slate-300 font-medium">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Depoimentos Section - Tom Escuro Novamente */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Overlay sutil para escurecer novamente */}


        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              O que nossos <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Clientes Dizem</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Depoimentos reais de clientes satisfeitos com nossos serviços
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group relative"
              >
                <div className="h-full p-8 rounded-2xl border border-white/10 hover:border-cyan-400/50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10">
                  {/* Aspas decorativas */}
                  <div className="text-6xl text-cyan-400/30 font-serif leading-none mb-4">"</div>

                  {/* Texto do depoimento */}
                  <p className="text-slate-200 leading-relaxed mb-6 relative z-10">
                    {testimonial.text}
                  </p>

                  {/* Estrelas */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Informações do cliente */}
                  <div className="border-t border-white/10 pt-4">
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-cyan-400 text-sm">{testimonial.company}</div>
                  </div>

                  {/* Efeito de brilho no hover */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Tom Final Equilibrado */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        {/* Overlay sutil para tom equilibrado */}
        <div className="absolute inset-0">
          {/* Elementos decorativos */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-cyan-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

          {/* Overlay gradual na parte inferior para transição suave com o footer */}
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#030617] via-[#030617]/70 to-transparent pointer-events-none"></div>
        </div>

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Título principal */}
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Transforme seu ambiente com
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Climatização Profissional
              </span>
            </h2>

            {/* Subtítulo */}
            <p className="text-xl md:text-2xl text-slate-300 mb-12 leading-relaxed">
              Solicite um orçamento personalizado e descubra como podemos criar o ambiente perfeito para você
            </p>

            {/* Benefícios em destaque */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-wrap justify-center gap-6 mb-12"
            >
              {[
                { icon: CheckCircle2, text: "Orçamento Gratuito" },
                { icon: Shield, text: "Garantia Estendida" },
                { icon: Clock, text: "Atendimento 24h" }
              ].map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="flex items-center gap-3 px-4 py-2 rounded-full border border-white/20">
                    <IconComponent className="w-5 h-5 text-cyan-400" />
                    <span className="text-white font-medium">{benefit.text}</span>
                  </div>
                );
              })}
            </motion.div>

            {/* Botões de ação */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            >
              <Link
                to="/contato"
                className="group relative px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg rounded-full shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 transform hover:scale-105 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <Phone className="w-6 h-6" />
                Solicitar Orçamento
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />

                {/* Efeito de brilho */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>

              <a
                href="https://wa.me/5513991028221"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-10 py-5 bg-white/10 backdrop-blur-sm text-white font-bold text-lg rounded-full border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all duration-300 flex items-center gap-3 min-w-[200px] justify-center"
              >
                <MessageCircle className="w-6 h-6" />
                WhatsApp Direto
              </a>
            </motion.div>

            {/* Informações de contato */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 text-slate-400"
            >
              <p className="text-sm">
                📞 Atendimento: Segunda a Sexta, 8h às 18h | Emergências 24h
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </React.Fragment>
  );
}
